import {
  createContext, useReducer, useEffect, useCallback,
  type Dispatch, type ReactNode, type FunctionComponent
} from 'react';
import { type AxiosResponse } from 'axios';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import FontFaceObserver from 'fontfaceobserver';
// Skillmore UI Components
import { isValidEmail } from '@empathco/ui-components/src/helpers/strings';
// local imports
import { AdminUser } from '../graphql/types';
import { APIActionResponse } from '../models/apiResponse';
import {
  SET_ACTIONS,
  AUTH, AUTHENTICATING, AUTHENTICATED, REAUTH, REAUTHENTICATING, REAUTHENTICATED,
  UNAUTHENTICATED, APP_ONLINE, CommonActions
} from '../constants/actionTypes';
import { isValidBuilderStep, BuilderStep } from '../constants/builder';
import { AuthErrorCode } from '../constants/authErrorCodes';
import useApiUrls, { axiosInstance as axios, API_USER_ME, API_EMAIL_AUTH_LINK } from '../config/api';
import { PATH_SUPERVISOR_EMPLOYEE } from '../config/paths';
import { ActionState, ContextObject } from '../models/contextEntity';
import { isManager, User } from '../models/user';
import useModels from '../helpers/models';
import { GlobalEChartsStyles } from '../config/params';
import { fetchFactory, APIResults } from '../helpers/actions';
import {
  getInitialActionState, getActionPendingState, getActionFinishedState,
  getInitialObjectState, getPendingObjectState, getFetchedObjectState, optimizeParams,
} from '../helpers/context';
import { updateEntity } from '../helpers/reducers';
import { removeAuthToken, setAuthToken, isLoggedIn, getAuthToken } from '../helpers/storage';
import { startFirebaseSamlLogin, getFirebaseIdToken } from '../config/firebase';
import useRedirects from '../helpers/redirect';
import { useMixpanel, currentEmployeeDetails } from './analytics';

export type GlobalEntityId = 'user';

export type Online = boolean | 'yes';
export type Token = string | boolean | null;

export type UserPaths = {
  supvEmplPath?: string;
}

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
export function setApolloClient(client: ApolloClient<NormalizedCacheObject>) {
  apolloClient = client;
}

const getUserPaths = (user?: User | null): UserPaths => user ? {
  ...isManager(user) ? {
    supvEmplPath: PATH_SUPERVISOR_EMPLOYEE
  } : {}
} : {};

export interface AuthEmailParams {
  email: string;
  onSuccess?: () => void;
}

export interface AuthenticateParams {
  token?: string | null;
}

export interface IGlobalState {
  online: Online;
  fontsLoaded: boolean;
  token: Token;
  authErrorCode?: AuthErrorCode | null;
  user: ContextObject<User, object>;
  paths: UserPaths;
  authEmail: ActionState<AuthEmailParams>;
  authenticate?: (params: AuthenticateParams) => void;
  loginWithFirebase?: () => Promise<void>;
  completeFirebaseLogin?: () => Promise<boolean>;
  unauthenticate?: () => void;
  sendAuthEmail?: (params: AuthEmailParams) => void;
  updateOnboardingSteps?: (step: string) => void;
}

interface IUserApiResponse {
  token: string;
  impersonation?: boolean;
  employee?: User;
  user?: AdminUser | null;
}

const authResult = ((data: Partial<IUserApiResponse>): IUserApiResponse | null => data && (data.employee || data.user) ? {
  token: data.token || getAuthToken(),
  employee: (data?.employee ? {
    ...data.employee,
    is_superuser: data.employee.is_superuser || data.employee.user?.is_superuser || data.user?.is_superuser,
    is_admin_only: false
  } as User : undefined) || (data?.user ? {
    ...data.user,
    code: data.user.username,
    is_admin_only: true
  } as User : undefined)
} : null) as APIResults<IUserApiResponse, IUserApiResponse>;

// fonts loading status update
const FONTS_LOADED = 'FONTS_LOADED' as const;
// cached User details update
const ONBOARDING_STEPS_UPDATE = 'ONBOARDING_STEPS_UPDATE' as const;
// Send Auth Email
const AUTH_EMAIL_SENDING = 'AUTH_EMAIL_SENDING' as const;
const AUTH_EMAIL_SENT = 'AUTH_EMAIL_SENT' as const;

export type GlobalActions =
  | { type: typeof AUTHENTICATING; payload?: null; params: AuthenticateParams; }
  | { type: typeof AUTHENTICATED; payload: Partial<IUserApiResponse> | null; params: AuthenticateParams;
      error_code?: AuthErrorCode; }
  | { type: typeof REAUTHENTICATING; payload?: null; params?: null; }
  | { type: typeof REAUTHENTICATED; payload: Partial<IUserApiResponse> | null; params?: null; }
  | { type: typeof AUTH_EMAIL_SENDING; payload?: boolean | null; params: AuthEmailParams; }
  | { type: typeof AUTH_EMAIL_SENT; payload: boolean; params: AuthEmailParams; }
  | { type: typeof FONTS_LOADED; payload?: null; params?: null; }
  | { type: typeof ONBOARDING_STEPS_UPDATE; payload: Record<BuilderStep, boolean>; params?: null; }
  | CommonActions;

type FirebaseExchangeParams = {
  firebaseToken: string;
  online: Online;
  dispatch: Dispatch<GlobalActions>;
  api: string;
  onSuccess: (employee?: User, noDefaultPath?: boolean) => void;
  onError: () => void;
};

// Swap a Firebase JWT for a DRF token, reporting whether it actually happened.
//
// Out here rather than inline in the action because the answer is not obvious:
// fetchFactory resolves whether or not the request worked — it catches every error,
// calls onError and dispatches FETCHED with a null payload — so awaiting it tells the
// caller nothing. Returning `true` regardless reported a 403 as a successful login,
// and the login screen re-armed its spinner and never ran its failure path.
//
// Whether `onSuccess` ran is the one reliable signal, and asserting the positive also
// covers the silent `if (!token || pending) return` bail-out, where nothing runs.
const exchangeFirebaseToken = async ({
  firebaseToken, online, dispatch, api, onSuccess, onError
}: FirebaseExchangeParams): Promise<boolean> => {
  let exchanged = false;
  await fetchFactory({
    token: firebaseToken,
    authScheme: 'Bearer',
    online,
    dispatch,
    type: AUTH,
    // `pending: false`, deliberately, where `authenticate` passes the live `pending`.
    // That flag means "this fetch is already in flight" — fetchFactory opens with
    // `if (!token || pending) return` and drops the call silently, no request and no
    // dispatch. The actions are rebuilt by an effect keyed on `pending`, so there is
    // one render where the context already reads `pending: false` while the closure
    // still holds `true`; a caller that fires exactly once on that render loses its
    // only attempt and waits forever. `authenticate` gets away with it by having no
    // such latch — it simply calls again next render. This exchange is one-shot and
    // explicitly initiated, so it says what is true: nothing is in flight yet.
    entity: { data: null, pending: false, failed: null, params: null } as ContextObject<User>,
    api,
    method: 'POST',
    results: authResult,
    onSuccess: (data) => {
      apolloClient?.resetStore();
      setAuthToken(data.token);
      onSuccess(data.employee, false);
      // Last, not first. fetchFactory runs this inside its own try, so a throw from
      // resetStore or onSuccess lands in the catch and is reported as a failure —
      // and this must not already have claimed the login succeeded, or the caller
      // is back to a spinner that never clears.
      exchanged = true;
    },
    onError
  })({});
  return exchanged;
};

// eslint-disable-next-line complexity
const globalReducer = (state: IGlobalState, action: GlobalActions): IGlobalState => {
  switch (action.type) {
    case SET_ACTIONS: return { ...state, ...action.payload };

    case APP_ONLINE: return { ...state, online: action.payload };

    case FONTS_LOADED: return state.fontsLoaded ? state : { ...state, fontsLoaded: true };

    case AUTHENTICATING:
    case REAUTHENTICATING:
      return {
        ...state,
        token: null,
        user: getPendingObjectState(action.params || {}),
        paths: {}
      };

    case AUTHENTICATED:
    case REAUTHENTICATED:
      return {
        ...state,
        token: action.payload?.token || null,
        authErrorCode: (action.type === AUTHENTICATED && action.error_code) || null,
        user: action.type === REAUTHENTICATED && !action.payload?.employee
          ? getInitialObjectState()
          : getFetchedObjectState(action.payload?.employee || null, action.params || {}),
        paths: getUserPaths(action.payload?.employee)
      };

    case AUTH_EMAIL_SENDING: return { ...state, authEmail: getActionPendingState(action.params) };
    case AUTH_EMAIL_SENT: return { ...state, authEmail: getActionFinishedState(action.payload, action.params) };

    case ONBOARDING_STEPS_UPDATE:
      return {
        ...state,
        ...action.payload ? updateEntity(state, 'user' as GlobalEntityId,
          (user: User | null) => user ? {
            ...user, onboarding_steps: { ...user.onboarding_steps || {}, ...action.payload }
          } as User : user) : {}
      };

    case UNAUTHENTICATED: return {
      ...state,
      token: null,
      user: getInitialObjectState(),
      paths: {}
    };

    default: return state;
  }
};

const initialGlobalState: IGlobalState = {
  online: true,
  fontsLoaded: false,
  token: null,
  authErrorCode: null,
  user: {
    data: null,
    pending: true, // we are waiting for `useEffect` hook that checks `isLoggedIn()`
    failed: null,
    params: null
  },
  paths: {},
  authEmail: getInitialActionState()
};

export const GlobalContext = createContext<IGlobalState>(initialGlobalState);

type GlobalProviderProps = {
  readonly children?: ReactNode;
  // for Storybook only
  readonly state?: IGlobalState;
  // for Jest specs only
  readonly initState?: IGlobalState;
};

export const GlobalProvider: FunctionComponent<GlobalProviderProps> = ({
  state,
  initState,
  children
}) => {
  const { API_USER_TOKEN_BY_SSO } = useApiUrls();
  const { getRedirectionPath } = useRedirects();
  const { getLocationStr } = useModels();

  const [globalState, dispatch] = useReducer(globalReducer, initState
    ? { ...initialGlobalState, ...initState } : initialGlobalState
  );
  const navigate = useNavigate();
  const [, , removeCookie] = useCookies<string>([]);
  const mixpanel = useMixpanel();

  const actualState = state || globalState;
  const { fontsLoaded, online, token, user: { pending }, authEmail: { pending: authEmailPending } } = actualState;

  function unauthenticate() {
    removeAuthToken();
    currentEmployeeDetails.code = undefined;
    currentEmployeeDetails.distinct_id = undefined;
    dispatch({ type: UNAUTHENTICATED });
    apolloClient?.resetStore();
  }

  function updateOnboardingSteps(step: string) {
    if (isValidBuilderStep(step)) dispatch({
      type: ONBOARDING_STEPS_UPDATE,
      payload: { [step]: true } as Record<BuilderStep, boolean>
    });
  }

  function networkChange() {
    dispatch({
      type: APP_ONLINE,
      payload: Boolean(window.navigator.onLine)
    });
  }

  function fontsLoadCompleted() {
    dispatch({ type: FONTS_LOADED });
  }

  const onSuccess = useCallback((employee?: User, noDefaultPath = false): void => {
    removeCookie('sessionid');
    removeCookie('saml_session');
    if (employee) {
      const { code, location, work_location, current_job } = employee;
      if (location) location.title = getLocationStr(location) || location.title;
      if (work_location) work_location.title = getLocationStr(work_location) || work_location.title;
      if (current_job?.location) {
        current_job.location.title = getLocationStr(current_job.location) || current_job.location.title;
      }
      currentEmployeeDetails.code = code;
      currentEmployeeDetails.distinct_id = code;
      mixpanel.identify(code);
      const path = getRedirectionPath(employee, noDefaultPath);
      if (path) navigate(path, { replace: true });
    } else {
      currentEmployeeDetails.code = undefined;
      currentEmployeeDetails.distinct_id = undefined;
    }
  }, [navigate, removeCookie, mixpanel, getRedirectionPath, getLocationStr]);

  const onError = useCallback((): void => {
    removeAuthToken();
    removeCookie('sessionid');
    removeCookie('saml_session');
    currentEmployeeDetails.code = undefined;
    currentEmployeeDetails.distinct_id = undefined;
  }, [removeCookie]);

  useEffect(() => {
    window.addEventListener('offline', networkChange);
    window.addEventListener('online', networkChange);
    // Font Loading
    if (!fontsLoaded) {
      const testString = 'BESÅãbswy' as const;
      const fontFamily = GlobalEChartsStyles.textStyle?.fontFamily || 'Roboto';
      Promise.all([
        // IMPORTANT! List of weights and styles must be in sync with imports in:
        // `@empathco/ui-components/src/styles/roboto.scss`
        new FontFaceObserver(fontFamily, { weight: 400 }).load(testString),
        new FontFaceObserver(fontFamily, { weight: 500 }).load(testString),
        new FontFaceObserver(fontFamily, { weight: 700 }).load(testString),
        new FontFaceObserver(fontFamily, { weight: 300 }).load(testString),
        new FontFaceObserver(fontFamily, { weight: 400, style: 'italic' }).load(testString),
        new FontFaceObserver(fontFamily, { weight: 500, style: 'italic' }).load(testString)
      ])
        .then(fontsLoadCompleted)
        .catch(fontsLoadCompleted);
    }
    dispatch({
      type: SET_ACTIONS,
      payload: {
        updateOnboardingSteps,
        unauthenticate
      }
    });
    return () => {
      window.removeEventListener('online', networkChange);
      window.removeEventListener('offline', networkChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // we do not need to monitor `fontsLoaded` here

  useEffect(() => {
    if (!token) {
      if (isLoggedIn()) {
        fetchFactory({
          token: getAuthToken(),
          online: true,
          dispatch,
          type: REAUTH,
          entity: getInitialObjectState<User, object>(),
          api: API_USER_ME,
          results: authResult,
          onSuccess: (data) => {
            apolloClient?.resetStore();
            onSuccess(data.employee, true);
          },
          onError
        })();
      } else {
        dispatch({ type: UNAUTHENTICATED });
      }
    }
  }, [token, onSuccess, onError]);

  useEffect(() => {
    dispatch({
      type: SET_ACTIONS,
      payload: {
        authenticate: fetchFactory({
          token: true,
          online,
          dispatch,
          params: ['token'],
          type: AUTH,
          entity: { data: null, pending, failed: null, params: null } as ContextObject<User>,
          api: API_USER_TOKEN_BY_SSO,
          method: 'POST',
          results: authResult,
          onSuccess: (data) => {
            apolloClient?.resetStore();
            setAuthToken(data.token);
            onSuccess(data.employee, false);
          },
          onError
        }),
        // Firebase-brokered SSO, step 1: leave the tab for the Firebase SAML
        // handler. Must be called from a click — this navigates the whole page.
        // Rejects if the navigation could not be started; `onError` only clears
        // the stored token, so the caller has to surface the failure itself or
        // the screen sits on a spinner forever.
        loginWithFirebase: async () => {
          try {
            await startFirebaseSamlLogin();
          } catch (error) {
            onError();
            throw error;
          }
        },
        // Firebase-brokered SSO, step 2: back from the handler (or with a session
        // Firebase still holds), exchange the Firebase JWT (Bearer) for a DRF
        // token at the same /token-by-sso/ endpoint. Resolves `false` when nobody
        // is signed in, so the caller can offer the login button instead, and
        // rejects when firebase failed or the exchange was refused — a signed-in
        // user the backend will not issue a token for is a failure, not a resume.
        completeFirebaseLogin: async () => {
          let firebaseToken: string | null;
          try {
            firebaseToken = await getFirebaseIdToken();
          } catch (error) {
            onError();
            throw error;
          }
          if (!firebaseToken) return false;
          const exchanged = await exchangeFirebaseToken({
            firebaseToken,
            online,
            dispatch,
            api: API_USER_TOKEN_BY_SSO,
            onSuccess,
            onError
          });
          if (!exchanged) throw new Error('SSO token exchange failed');
          return true;
        }
      }
    });
  }, [online, pending, onSuccess, onError, API_USER_TOKEN_BY_SSO]);

  useEffect(() => {
    dispatch({ type: SET_ACTIONS, payload: {
      sendAuthEmail: async ({
        email,
        onSuccess: onEmailSent
      }: AuthEmailParams = {} as AuthEmailParams) => {
        if (authEmailPending || !isValidEmail(email)) return;
        const params = optimizeParams({ email }, online);
        try {
          if (!online) throw new Error();
          dispatch({
            type: AUTH_EMAIL_SENDING,
            params
          });
          const { status, data } = await axios.request<AuthEmailParams, AxiosResponse<APIActionResponse>>({
            method: 'POST',
            url: API_EMAIL_AUTH_LINK,
            data: params
          }) || {};
          if (status < 200 || status > 201 || !data || !data.success) throw new Error();
          onEmailSent?.();
          dispatch({
            type: AUTH_EMAIL_SENT,
            payload: true,
            params
          });
        } catch (_error) {
          dispatch({
            type: AUTH_EMAIL_SENT,
            payload: false,
            params
          });
        }
      }
    }});
  }, [authEmailPending, online]);

  return (
    <GlobalContext value={actualState}>
      {children}
    </GlobalContext>
  );
};
